import { useEffect, useRef, useState, memo } from "react";
import cls from "classnames";
import { Skeleton } from "antd";
import PDFObject from "pdfobject";
import { Book } from "epubjs";
import { getFileDetail } from "../../../../services/richmodal";
import * as marked from "marked";
import i18next from "../../../../i18n";
import { ReactComponent as CloseIcon } from "logic/src/components/icons/cross.svg";
import { ReactComponent as ScaleUpIcon } from "../../../../components/icons/scaleUp.svg";
import { ReactComponent as ScaleDownIcon } from "../../../../components/icons/scaleDown.svg";
import styles from "./index.module.scss";
import PdfViewer from "logic/src/components/PdfViewer";
import { ISelection, EFileQueryType } from "logic/src/objects/chat/types";

interface IProps {
  fileUrl?: string;
  fileId?: string;
  fileName?: string;
  className?: string;
  currentSelection?: ISelection;
  highlightList?: ISelection[];
  onQuery?: (v: EFileQueryType, selection: ISelection) => void;
  onCut?: (v: File) => void;
  onDeleteSelection?: (v: ISelection & { msgId?: string }) => void;
  onClose?: () => void;
  hideActionBar?: boolean;
  readOnly?: boolean;
}

enum EStatus {
  init,
  error,
  canNotPreview,
  preview,
}

const MessageMap = {
  [EStatus.canNotPreview]: i18next.t("sensechat-131"), //  "该文档不支持在线预览…",
  [EStatus.error]: i18next.t("sensechat-132"), //"发生错误",
  [EStatus.init]: "",
  [EStatus.preview]: "",
};

enum EScaleType {
  up = "up",
  down = "down",
}

const SCALE_STEP = 0.5;
const IMAGE_DOM_ID = "chatPreviewImage";

const microSoftFile = (name: string) => {
  return (
    name.endsWith(".doc") ||
    name.endsWith(".docx") ||
    name.endsWith(".pptx") ||
    name.endsWith(".ppt")
  );
};

const FileDisplay = ({
  fileUrl,
  fileId,
  fileName,
  className,
  currentSelection,
  highlightList,
  onQuery,
  onCut,
  onDeleteSelection,
  onClose,
  hideActionBar,
  readOnly,
}: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<EStatus>(EStatus.init);

  const showFile = async (
    urlProp: string | undefined,
    fileId: string | undefined,
  ) => {
    if (!urlProp && !fileId) {
      throw new Error(
        "FileDisplay must have at least one of the properties: fileUrl or fileId.",
      );
    }
    setStatus(EStatus.init);
    let name = fileName || urlProp || "";

    name = name.toLowerCase();

    if (
      name.endsWith(".pdf") ||
      name.endsWith(".epub") ||
      name.endsWith(".txt") ||
      name.endsWith(".doc") ||
      name.endsWith(".docx") ||
      name.endsWith(".ppt") ||
      name.endsWith(".pptx") ||
      name.endsWith(".md") ||
      name.endsWith(".link") ||
      name.endsWith(".image")
    ) {
      setStatus(EStatus.preview);
    } else {
      setStatus(EStatus.canNotPreview);
      return () => {};
    }
    let url: string = urlProp || "";
    if (!url || (microSoftFile(name) && url.toString().startsWith("blob"))) {
      if (fileId) {
        const res = await getFileDetail(fileId);
        url = res.url;
      }
    }

    if (name.endsWith(".pdf")) {
      if (readOnly) {
        containerRef.current
          ? (containerRef.current.innerHTML = `
          <iframe
            src="${url}"
            width="100%"
            height="100%"
            frameborder="0"
          ></iframe>
          `)
          : null;

        return () =>
          containerRef.current ? (containerRef.current.innerHTML = "") : "";
      } else {
        PDFObject.embed(url, containerRef.current);
        return () =>
          containerRef.current
            ? (containerRef.current.innerHTML = "<div></div>")
            : "";
      }
    } else if (name.endsWith(".epub")) {
      const res = await window
        .fetch(url, {
          method: "get",
        })
        .then(res => {
          return res.arrayBuffer();
        });
      // @ts-ignore
      const book = new Book(res);
      const rendition = book.renderTo(containerRef.current!, {
        manager: "continuous",
        flow: "scrolled",
        width: "100%",
        height: "100%",
      });
      rendition.display();

      return () => rendition.destroy();
    } else if (name.endsWith(".txt") || name.endsWith(".link")) {
      containerRef.current
        ? (containerRef.current.innerHTML = `
          <iframe
            src="${url}"
            width="100%"
            height="100%"
            frameborder="0"
          ></iframe>
          `)
        : null;

      return () =>
        containerRef.current ? (containerRef.current.innerHTML = "") : "";
    } else if (name.endsWith(".image")) {
      containerRef.current
        ? (containerRef.current.innerHTML = `
          <div style="height: 100%; width: 100%; overflow: auto; display: flex; align-items: center; justify-content: center;">
            <img
              src="${url}"
              data-scale="1"
              id="${IMAGE_DOM_ID}"
              style="max-width: 100%; max-height: 100%; object-fit: contain; transform: scale(1);"
            />
          </div>
          `)
        : null;
      return () =>
        containerRef.current ? (containerRef.current.innerHTML = "") : "";
    } else if (microSoftFile(name)) {
      containerRef.current
        ? (containerRef.current.innerHTML = `
      <iframe
        src="https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}"
        width="100%"
        height="100%"
        frameborder="0"
      ></iframe>
      `)
        : null;

      return () =>
        containerRef.current ? (containerRef.current.innerHTML = "") : "";
    } else if (name.endsWith(".md")) {
      const text = await window
        .fetch(url, {
          method: "get",
        })
        .then(response => response.text());

      containerRef.current
        ? (containerRef.current.innerHTML = marked.parse(text))
        : null;

      return () =>
        containerRef.current ? (containerRef.current.innerHTML = "") : "";
    }

    return () => {};
  };
  useEffect(() => {
    if (!fileName?.endsWith(".pdf") || readOnly) {
      const clearOb = { current: () => {} };
      try {
        showFile(fileUrl, fileId)
          .then(fn => (clearOb.current = fn))
          .catch(() => setStatus(EStatus.error));
      } catch (e) {
        setStatus(EStatus.error);
      }
      return () => clearOb.current();
    }
  }, [fileUrl, fileId, fileName, readOnly]);

  const handleImgScale = (scale: EScaleType) => {
    const imgDom = document.getElementById(IMAGE_DOM_ID);
    if (!imgDom) return;

    let currentScale = +(imgDom.dataset.scale || 1);
    scale === EScaleType.up
      ? (currentScale += SCALE_STEP)
      : (currentScale -= SCALE_STEP);
    if (currentScale < 0.1) {
      currentScale = 0.1;
    }
    imgDom.dataset.scale = `${currentScale}`;
    imgDom.style.transform = `scale(${currentScale})`;
  };

  const handleCloseFile = () => {
    onClose?.();
  };

  const tip = MessageMap[status];
  const showActionBox = !hideActionBar && !fileName?.endsWith(".pdf");

  return (
    <div className={cls(styles.fileDisplay, className)}>
      {showActionBox && (
        <div className={styles.toolBox}>
          {fileName?.endsWith(".image") && (
            <>
              <ScaleUpIcon
                className={styles.toolIcon}
                onClick={() => handleImgScale(EScaleType.up)}
              />
              <ScaleDownIcon
                className={styles.toolIcon}
                onClick={() => handleImgScale(EScaleType.down)}
              />
            </>
          )}
          <CloseIcon className={styles.toolIcon} onClick={handleCloseFile} />
        </div>
      )}
      {!readOnly && fileName?.endsWith(".pdf") && (
        <PdfViewer
          onCloseFile={handleCloseFile}
          onDeleteSelection={onDeleteSelection}
          onQuery={onQuery}
          readOnly={readOnly}
          onCut={onCut}
          hideActionBar={hideActionBar}
          url={fileUrl!}
          currentSelection={currentSelection}
          highlightList={highlightList}
        />
      )}
      {(!fileName?.endsWith(".pdf") || readOnly) && (
        <>
          <div
            className={cls(
              styles.fileContainer,
              status === EStatus.preview && styles.preview,
            )}
            ref={containerRef}
          ></div>
          {status !== EStatus.preview && (
            <div className={styles.info}>
              <Skeleton active={status === EStatus.init} />
              <Skeleton active={status === EStatus.init} />
              <div className={styles.tip}>{tip}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default memo(FileDisplay);
